import React from "react";
import { Form, ModalDialog, StringField, SubmitButton } from "@culturehq/components";

import { useDoor, useTryCatch } from "app/hooks";
import { Trans } from "app/utils";
import ErrorMessage from "components/common/ErrorMessage";
import ChqModalDialog from "components/common/ChqModalDialog";

import { Context } from "./AIContextTypes";

interface AIContextModalProps {
  children: (callback: () => void) => React.ReactNode;
  context?: Context;
  onCreate?: (context: Context) => void;
  onUpdate?: (context: Context) => void;
}

const AIContextModal = ({
  children,
  context,
  onCreate = () => {},
  onUpdate = () => {}
}: AIContextModalProps) => {
  const { open, onOpen, onClose } = useDoor(false);

  const [{ error }, onSubmit] = useTryCatch(
    async (values: Partial<Context>) => {
      if (context) onUpdate({ ...context, ...values });
      else onCreate(values as Context);

      onClose();
    },
    [context]
  );

  if (error && error.status !== 422) {
    throw error;
  }

  const ModalTitle = () => (
    <Trans>
      {context ? "Edit context" : "Add context"}
    </Trans>
  );

  return (
    <>
      {children(onOpen)}
      {open && (
        <ChqModalDialog onClose={onClose}>
          <ModalDialog.Heading onClose={onClose}>
            <ModalTitle />
          </ModalDialog.Heading>
          <ModalDialog.Body>
            {error && <ErrorMessage error={error} />}
            <Form onSubmit={onSubmit} initialValues={context}>
              <StringField
                name="key"
                autoFocus
                required
              >
                <Trans>Context Key</Trans>
              </StringField>
              <StringField
                name="value"
                required
              >
                <Trans>Context Value</Trans>
              </StringField>
              <SubmitButton primary>
                {submitting => (
                  submitting ? <Trans>Submitting...</Trans> : <Trans>Save</Trans>
                )}
              </SubmitButton>
            </Form>
          </ModalDialog.Body>
        </ChqModalDialog>
      )}
    </>
  );
};

export default AIContextModal;
